import { ReactElement, useState, ChangeEventHandler, useContext } from "react";
import { useNavigate } from "react-router-dom";

import { FormField, PasswordInput } from "@justworkshr/milo-form";
import { Button } from "@justworkshr/milo-core";

import styles from "./changePassword.module.css";

import { PATH_TO_HOME } from "constants/navigation";
import { SUCCESSFUL_PASSWORD_CHANGE } from "constants/pageAlertsCopy";

import useChangePassword from "hooks/useChangePassword";
import useUspFlow from "hooks/useUspFlow";

import PageAlertContext from "contexts/PageAlertContext";

import PageCard from "global-components/pageCard/PageCard";

const ChangePassword = (): ReactElement => {
  useUspFlow("changePassword");

  const [oldPasswordInput, setOldPasswordInput] = useState<string>("");
  const [newPasswordInput, setNewPasswordInput] = useState<string>("");
  const [retypedPasswordInput, setRetypedPasswordInput] = useState<string>("");

  const [inlinePasswordError, setPasswordInlineError] = useState<string>("");
  const clearInlineError = () => setPasswordInlineError("");

  const { mutate: saveChangedPassword } = useChangePassword();
  const { setAlert } = useContext(PageAlertContext);
  const navigate = useNavigate();

  const handleSubmitForm: ChangeEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    clearInlineError();

    if (newPasswordInput !== retypedPasswordInput) {
      setPasswordInlineError(
        "Your retyped new password does not match the new password",
      );
      return;
    }
    saveChangedPassword(
      {
        oldPassword: oldPasswordInput,
        newPassword: newPasswordInput,
      },
      {
        onSuccess: () => {
          navigate(PATH_TO_HOME);
          setAlert({
            message: SUCCESSFUL_PASSWORD_CHANGE,
            color: "additive",
          });
        },
        onError: (error) => {
          setPasswordInlineError(error?.errorCauses[0]?.errorSummary ?? "");
        },
      },
    );
  };

  return (
    <PageCard showBackToHomeLink={true} title="Change your password">
      <form onSubmit={handleSubmitForm}>
        <div className={styles.passwordField}>
          <FormField label="Old password" required={true}>
            <PasswordInput
              name="OldPassword"
              onChange={(e) => {
                clearInlineError();
                setOldPasswordInput(e.target.value);
              }}
              required={true}
            />
          </FormField>
        </div>
        <div className={styles.passwordField}>
          <FormField label="New password" required={true}>
            <PasswordInput
              name="NewPassword"
              onChange={(e) => {
                clearInlineError();
                setNewPasswordInput(e.target.value);
              }}
              required={true}
            />
          </FormField>
        </div>
        <div className={styles.passwordField}>
          <FormField
            label="Retype new password"
            required={true}
            error={inlinePasswordError}
          >
            <PasswordInput
              name="RetypedPassword"
              onChange={(e) => {
                clearInlineError();
                setRetypedPasswordInput(e.target.value);
              }}
              required={true}
            />
          </FormField>
        </div>
        <Button className={styles.button} size="md" type="submit">
          Save
        </Button>
      </form>
    </PageCard>
  );
};

export default ChangePassword;
